import type { CreateCallValues } from '../../lib/createCallSchema';

/**
 * Reading a dispatch text into a call, for the dispatch chat. Ported from
 * Mike's "Relay Dispatch Chat" prototype: a pasted police rotation, a motor
 * club text or a few typed words become the fields of a call. Pure, so the
 * reading can be tested without a screen.
 */

export type DraftPriority = 'NORMAL' | 'HIGH' | 'URGENT';
export type DraftWeight = '' | 'LIGHT' | 'MEDIUM' | 'HEAVY';

export interface DraftCall {
  /** The customer as the text names it: "Santa Clara PD". */
  account: string;
  /** The time the text says it came in, as written. */
  received: string;
  pickup: string;
  destination: string;
  year: string;
  make: string;
  model: string;
  color: string;
  plate: string;
  vin: string;
  towType: DraftWeight;
  contactName: string;
  contactPhone: string;
  /** The sender's own number for it: a police event number. */
  dispatchNumber: string;
  reason: string;
  priority: DraftPriority;
}

export interface ParsedCall {
  call: DraftCall;
  /** Lines the text had that no field takes, as "Label: value". */
  notes: string[];
  /** How many fields were filled. */
  filled: number;
  /** False for free text with no "Label: value" lines. */
  structured: boolean;
}

const MONTHS = [
  'January',
  'February',
  'March',
  'April',
  'May',
  'June',
  'July',
  'August',
  'September',
  'October',
  'November',
  'December',
];

const DATE_RE =
  /^(\d{1,2})\/(\d{1,2})\/(\d{2,4})[ T]+(\d{1,2}):(\d{2})(?::\d{2})?\s*(AM|PM)?$/i;

export const WEIGHT_LABEL: Record<Exclude<DraftWeight, ''>, string> = {
  LIGHT: 'Light',
  MEDIUM: 'Medium',
  HEAVY: 'Heavy',
};

export const PRIORITY_LABEL: Record<DraftPriority, string> = {
  NORMAL: 'Normal',
  HIGH: 'High',
  URGENT: 'Urgent',
};

type FieldKey = keyof DraftCall | 'vehicle';

const KEYMAP: [RegExp, FieldKey][] = [
  [
    /^(event|incident|case|dispatch|call)\s*(#|no\.?|number)?$/i,
    'dispatchNumber',
  ],
  [/^(ph|phone|tel|callback|contact phone)\.?$/i, 'contactPhone'],
  [/^weight( class)?$/i, 'towType'],
  [/^(vehicle|veh|car)$/i, 'vehicle'],
  [/^year$/i, 'year'],
  [/^make$/i, 'make'],
  [/^model$/i, 'model'],
  [/^colou?r$/i, 'color'],
  [/^(plate|license plate|lp)$/i, 'plate'],
  [/^vin$/i, 'vin'],
  [/^(pickup|pick up|pickup location|location|loc)$/i, 'pickup'],
  [/^(destination|dest|drop ?off|tow to)$/i, 'destination'],
  [/^(accident or storage|reason)$/i, 'reason'],
  [/^(contact|caller|contact name)$/i, 'contactName'],
];

const NOTE_LABELS: [RegExp, string][] = [
  [/^pickup x ?-?st(reet)?$/i, 'Cross street'],
  [/^rotation ?\/ ?request$/i, 'Request type'],
  [/^rotation$/i, 'Request type'],
  [/^rollover$/i, 'Rollover'],
  [/^blocking lane$/i, 'Blocking lane'],
];

export function blankCall(): DraftCall {
  return {
    account: '',
    received: '',
    pickup: '',
    destination: '',
    year: '',
    make: '',
    model: '',
    color: '',
    plate: '',
    vin: '',
    towType: '',
    contactName: '',
    contactPhone: '',
    dispatchNumber: '',
    reason: '',
    priority: 'NORMAL',
  };
}

/** "10/12/2026 3:37:30 AM" as "October 12 @ 3:37 AM"; anything else as is. */
export function fmtReceived(value: string): string {
  const m = String(value).trim().match(DATE_RE);
  if (!m) return String(value);
  const month = MONTHS[Number(m[1]) - 1];
  if (!month) return String(value);
  let hour = Number(m[4]);
  const ampm = m[6] ? m[6].toUpperCase() : hour >= 12 ? 'PM' : 'AM';
  if (!m[6] && hour > 12) hour -= 12;
  return `${month} ${Number(m[2])} @ ${hour}:${m[5]} ${ampm}`;
}

function towFrom(value: string): DraftWeight {
  const v = value.toLowerCase();
  if (/light|motorcycle|bike/.test(v)) return 'LIGHT';
  if (/medium|class ?d/.test(v)) return 'MEDIUM';
  if (/heavy|tractor|trailer|equipment/.test(v)) return 'HEAVY';
  return '';
}

/** "2019 Toyota Corolla" into its year, make and model. */
export function parseVehicle(value: string): {
  year: string;
  make: string;
  model: string;
} {
  const words = String(value).trim().split(/\s+/).filter(Boolean);
  let year = '';
  if (words.length && /^(19|20)\d{2}$/.test(words[0])) year = words.shift()!;
  const make = words.shift() ?? '';
  return { year, make, model: words.join(' ') };
}

export function vehicleText(call: DraftCall): string {
  return [call.year, call.make, call.model].filter(Boolean).join(' ');
}

function sentence(value: string): string {
  const s = value.trim();
  return s.charAt(0).toUpperCase() + s.slice(1);
}

function noteLabel(label: string): string {
  const hit = NOTE_LABELS.find(([re]) => re.test(label));
  return hit ? hit[1] : sentence(label);
}

/** "SANTA CLARA PD" as "Santa Clara PD". */
export function titleCase(value: string): string {
  return String(value)
    .toLowerCase()
    .replace(/\b([a-z])/g, (_, c: string) => c.toUpperCase())
    .replace(/\b(Pd|Chp|Vin)\b/g, (m) => m.toUpperCase());
}

/**
 * A pickup as its street and its town: "DE LA CRUZ AND CENTRAL, SANTA
 * CLARA" is "De La Cruz & Central" in "Santa Clara".
 */
export function splitPickup(pickup: string): { street: string; city: string } {
  const bits = String(pickup).split(',');
  let street = (bits.shift() ?? '').trim();
  const city = bits.join(',').trim();
  // Already cased, as Relay AI writes it: keep it as written. Only a
  // dispatch text in capitals ("DE LA CRUZ AND CENTRAL") is recased.
  if (/[a-z]/.test(pickup)) return { street, city };
  const parts = street
    .split(/\s+(?:AND|&|@|AT)\s+/i)
    .map((s) => s.trim())
    .filter(Boolean);
  street =
    parts.length === 2
      ? `${titleCase(parts[0])} & ${titleCase(parts[1])}`
      : titleCase(street);
  return { street, city: titleCase(city) };
}

/** The pickup as one line: "De La Cruz & Central, Santa Clara". */
export function pickupLine(pickup: string): string {
  const { street, city } = splitPickup(pickup);
  return city ? `${street}, ${city}` : street;
}

/**
 * A dispatch text as a call. Lines of "Label: value" fill the fields they
 * name; a timestamp line is when it came in; the first bare line is the
 * customer; anything else becomes a note. Text with no labelled lines is
 * free text: the whole of it is kept as a note, with any phone number in it.
 */
export function parseCall(text: string): ParsedCall {
  const call = blankCall();
  const notes: string[] = [];
  let filled = 0;
  const lines = String(text)
    .split(/\r?\n/)
    .map((s) => s.trim())
    .filter(Boolean);
  const keyed = lines.filter(
    (l) => !DATE_RE.test(l) && /^[^:]{1,40}:\s*\S/.test(l)
  ).length;

  if (keyed === 0) {
    const phone = String(text).match(
      /(\+?1?[\s.-]?\(?\d{3}\)?[\s.-]\d{3}[\s.-]\d{4})/
    );
    if (phone) {
      call.contactPhone = phone[1].trim();
      filled++;
    }
    notes.push(`Message: ${lines.join(' ')}`);
    return { call, notes, filled, structured: false };
  }

  for (const line of lines) {
    if (DATE_RE.test(line)) {
      call.received = line;
      filled++;
      continue;
    }
    const m = line.match(/^([^:]{1,40}):\s*(.+)$/);
    if (!m) {
      if (!call.account) {
        call.account = line;
        filled++;
      } else notes.push(line);
      continue;
    }
    const label = m[1].replace(/\?$/, '').trim();
    const value = m[2].trim();
    const hit = KEYMAP.find(([re]) => re.test(label));
    if (hit?.[1] === 'towType') {
      const weight = towFrom(value);
      if (weight) {
        call.towType = weight;
        filled++;
        continue;
      }
    } else if (hit?.[1] === 'vehicle') {
      Object.assign(call, parseVehicle(value));
      filled++;
      continue;
    } else if (hit && hit[1] !== 'priority') {
      (call as unknown as Record<string, string>)[hit[1]] = value;
      filled++;
      continue;
    }
    if (/blocking/i.test(label) && /^(y|yes|true)/i.test(value))
      call.priority = 'HIGH';
    notes.push(`${noteLabel(label)}: ${value}`);
  }
  return { call, notes, filled, structured: true };
}

/**
 * The call's notes as the server keeps them: the lines no field took, plus
 * what the create payload has no field for. The sender's event number and
 * the time it came in go here, and the customer as the text named it when
 * that is not the account the call is on.
 */
export function notesFor(
  call: DraftCall,
  notes: readonly string[],
  accountName?: string
): string {
  const extra: string[] = [];
  if (call.dispatchNumber) extra.push(`Event #: ${call.dispatchNumber}`);
  if (call.received) extra.push(`Received: ${fmtReceived(call.received)}`);
  if (
    call.account &&
    call.account.trim().toLowerCase() !== accountName?.trim().toLowerCase()
  )
    extra.push(`Account as sent: ${call.account}`);
  return [...extra, ...notes].join('\n');
}

/**
 * The customer the text names, among the company's accounts: the same name
 * first, then one whose name holds it or is held by it. Null when none.
 */
export function matchAccount<A extends { id: string; accountName: string }>(
  name: string,
  accounts: readonly A[]
): A | null {
  const want = name.trim().toLowerCase();
  if (!want) return null;
  return (
    accounts.find((a) => a.accountName.trim().toLowerCase() === want) ??
    accounts.find((a) => {
      const have = a.accountName.trim().toLowerCase();
      return have.length > 2 && (have.includes(want) || want.includes(have));
    }) ??
    null
  );
}

/**
 * The parsed call as the create sheet's form values, so it posts through
 * the same toCreateCallRequest and useCreateCall the sheet does. `account`
 * is the account's id; `drivers` the user ids to put on the call, none to
 * leave it unassigned; `dispatchNumber` the company's own number for it.
 */
export function toCreateValues(
  call: DraftCall,
  notes: readonly string[],
  {
    accountId,
    accountName,
    drivers,
    dispatchNumber,
  }: {
    accountId: string;
    accountName?: string;
    drivers: string[];
    dispatchNumber: string;
  }
): CreateCallValues {
  return {
    account: accountId,
    priority: call.priority,
    callType: 'new',
    towType: call.towType || 'ANY',
    reason: call.reason,
    dispatchNumber,
    contactName: call.contactName,
    contactPhone: call.contactPhone,
    pickupLocation: call.pickup ? pickupLine(call.pickup) : '',
    destination: call.destination,
    vehicleMake: call.make,
    vehicleModel: call.model,
    vehicleYear: call.year,
    vehicleColor: call.color,
    licensePlate: call.plate,
    vin: call.vin,
    drivers,
    notes: notesFor(call, notes, accountName),
    completionTime: '',
    scheduledDate: '',
    scheduledTime: '',
  };
}
